import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { ListTree, AlertOctagon, ShieldCheck } from 'lucide-react';

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '—');

const ItemLotTrackingCard = ({ itemId, baseUom }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api().get(`/items/${itemId}/lots`)
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setData(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [itemId, api]);

  return (
    <Card className="industrial-card" data-testid="item-lot-tracking-card">
      <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <ListTree className="w-4 h-4" /> Lot / Tracking History
        </h2>
        {data && (
          <span className="text-xs text-slate-500 mono" data-testid="lot-card-totals">
            {data.totals.lot_count} lot{data.totals.lot_count === 1 ? '' : 's'}
            {data.totals.active_recalls > 0 && (
              <span className="ml-2 text-red-600">· {data.totals.active_recalls} recall{data.totals.active_recalls === 1 ? '' : 's'}</span>
            )}
          </span>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 text-center text-sm text-slate-400">Loading…</div>
        ) : !data || data.lots.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500" data-testid="lot-card-empty">
            No batch / lot numbers recorded yet. Use <span className="font-medium">Restock</span> to capture a lot.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Lot / Tracking #</TableHead>
                  <TableHead>First Received</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Disposed</TableHead>
                  <TableHead className="text-right">On Hand</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.lots.map((l) => (
                  <TableRow key={l.lot} data-testid={`lot-row-${l.lot}`}>
                    <TableCell className="mono font-medium text-slate-800">{l.lot}</TableCell>
                    <TableCell className="text-sm text-slate-600" data-testid={`lot-first-received-${l.lot}`}>{fmtDate(l.first_received_at)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{fmtDate(l.last_activity_at)}</TableCell>
                    <TableCell className="text-right mono text-emerald-700">{l.total_received}</TableCell>
                    <TableCell className="text-right mono text-red-700">{l.total_disposed}</TableCell>
                    <TableCell className="text-right mono">{l.on_hand} {baseUom}</TableCell>
                    <TableCell>
                      {l.active_recall ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 border border-red-200 rounded-sm text-xs">
                          <AlertOctagon className="w-3 h-3" /> Recalled
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm text-xs">
                          <ShieldCheck className="w-3 h-3" /> Clear
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-sm"
                        onClick={() => navigate(`/batch-lot-recall?lot=${encodeURIComponent(l.lot)}`)}
                        data-testid={`lot-trace-${l.lot}`}
                      >
                        Trace
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ItemLotTrackingCard;
